// OneSignal Web SDK (v16) integration. The CDN page-script is loaded in
// public/index.html; here we init once and expose small helpers used by the
// notification preferences UI. App ID comes from the environment (public value).
const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

const deferred = (fn) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(fn);
};

let _inited = false;

export function initOneSignal() {
    if (_inited || !APP_ID) return;
    _inited = true;
    deferred(async (OneSignal) => {
        try {
            await OneSignal.init({
                appId: APP_ID,
                allowLocalhostAsSecureOrigin: true,
                serviceWorkerPath: "OneSignalSDKWorker.js",
                serviceWorkerParam: { scope: "/" },
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("OneSignal init failed", e);
        }
    });
}

// Link this browser's subscription to the logged-in Rosterly account.
export function osLogin(userId) {
    if (!APP_ID || !userId) return;
    deferred(async (OneSignal) => {
        try { await OneSignal.login(String(userId)); } catch { /* noop */ }
    });
}

export function osLogout() {
    if (!APP_ID) return;
    deferred(async (OneSignal) => {
        try { await OneSignal.logout(); } catch { /* noop */ }
    });
}

// Prompt the browser permission dialog + opt the user in. Resolves to the
// opted-in boolean.
export function promptPush() {
    return new Promise((resolve) => {
        if (!APP_ID) return resolve(false);
        deferred(async (OneSignal) => {
            try {
                if (!OneSignal.Notifications.isPushSupported()) return resolve(false);
                await OneSignal.Notifications.requestPermission();
                const optedIn = OneSignal.User?.PushSubscription?.optedIn;
                if (optedIn === false) { try { await OneSignal.User.PushSubscription.optIn(); } catch { /* noop */ } }
                resolve(!!OneSignal.User?.PushSubscription?.optedIn);
            } catch { resolve(false); }
        });
    });
}

export function optOutPush() {
    if (!APP_ID) return;
    deferred(async (OneSignal) => {
        try { await OneSignal.User.PushSubscription.optOut(); } catch { /* noop */ }
    });
}

// Report current subscription state: { supported, optedIn, permission }.
export function getPushState() {
    return new Promise((resolve) => {
        if (!APP_ID) return resolve({ supported: false, optedIn: false, permission: "unsupported" });
        deferred((OneSignal) => {
            try {
                resolve({
                    supported: OneSignal.Notifications.isPushSupported(),
                    optedIn: !!OneSignal.User?.PushSubscription?.optedIn,
                    permission: OneSignal.Notifications.permission ? "granted" : "default",
                });
            } catch { resolve({ supported: false, optedIn: false, permission: "unsupported" }); }
        });
    });
}

// Replace all OneSignal tags with the given map (values coerced to "1"/"0"
// strings; "0" tags are removed so segments stay clean).
export function osSyncTags(tagMap) {
    if (!APP_ID) return;
    deferred(async (OneSignal) => {
        try {
            const add = {}; const remove = [];
            Object.entries(tagMap || {}).forEach(([k, v]) => {
                if (v) add[k] = "1"; else remove.push(k);
            });
            if (Object.keys(add).length) await OneSignal.User.addTags(add);
            if (remove.length) await OneSignal.User.removeTags(remove);
        } catch { /* noop */ }
    });
}
